import { useState } from 'react';
import SignaturePad from './SignaturePad';
import { AREAS, getLineLabel, getUnitLabel } from '../../config/roasterySpec';

const formatCurrency = (v) => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(v || 0);

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  fontSize: '14px',
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border-color 0.15s'
};

function lineTotal(line) {
  const discount = (line.DiscountPercent || 0) / 100;
  return (line.UnitSellingPrice || 0) * (line.Quantity || 0) * (1 - discount);
}

function resolveLabel(areaConfigs, areaCode, lineNumber, unitNumber) {
  // Check for custom label in areaConfigs (most specific match first)
  if (unitNumber != null && unitNumber !== 0) {
    const cfg = areaConfigs.find((c) => c.AreaCode === areaCode && c.LineNumber === lineNumber && c.UnitNumber === unitNumber);
    if (cfg?.CustomLabel) return cfg.CustomLabel;
  }
  if (lineNumber != null) {
    const cfg = areaConfigs.find((c) => c.AreaCode === areaCode && c.LineNumber === lineNumber && c.UnitNumber == null);
    if (cfg?.CustomLabel) return cfg.CustomLabel;
  }
  const cfg = areaConfigs.find((c) => c.AreaCode === areaCode && c.LineNumber == null && c.UnitNumber == null);
  if (cfg?.CustomLabel) return cfg.CustomLabel;
  return null;
}

function getBreadcrumb(areaConfigs, line) {
  const parts = [];
  // Line label
  const customLine = resolveLabel(areaConfigs, line.AreaCode, line.LineNumber, null);
  parts.push(customLine || getLineLabel(line.LineNumber));
  // Unit label (skip if 0 / default)
  if (line.UnitNumber && line.UnitNumber !== 0) {
    const customUnit = resolveLabel(areaConfigs, line.AreaCode, line.LineNumber, line.UnitNumber);
    parts.push(customUnit || getUnitLabel(line.UnitNumber));
  }
  return parts.join(' \u203A ');
}

function getAreaSortOverride(areaConfigs, areaCode) {
  const config = (areaConfigs || []).find((c) => c.AreaCode === areaCode && c.LineNumber == null && c.UnitNumber == null);
  const parsed = Number.parseInt(config?.SortOrder, 10);
  return Number.isInteger(parsed) ? parsed : null;
}

function groupByArea(optionalLines, areaConfigs) {
  const groups = {};
  for (const line of optionalLines) {
    const code = line.AreaCode || '???';
    if (!groups[code]) {
      const customArea = resolveLabel(areaConfigs, code, null, null);
      groups[code] = {
        code,
        label: customArea || AREAS[code]?.label || code,
        order: getAreaSortOverride(areaConfigs, code) ?? AREAS[code]?.order ?? 99,
        lines: []
      };
    }
    groups[code].lines.push(line);
  }
  return Object.values(groups).sort((a, b) => a.order - b.order);
}

export default function AcceptanceForm({ quotation, optionalLines = [], areaConfigs = [], onSubmit, onCancel, loading }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [signature, setSignature] = useState(null);
  const [error, setError] = useState('');
  const [optionSelections, setOptionSelections] = useState(() => Object.fromEntries(optionalLines.map((l) => [l.ID, false])));

  const toggleOption = (lineId) => {
    setOptionSelections((prev) => ({ ...prev, [lineId]: !prev[lineId] }));
  };

  const selectedOptionsTotal = optionalLines.reduce((sum, l) => sum + (optionSelections[l.ID] ? lineTotal(l) : 0), 0);

  const baseTotal = quotation.totals?.standard || 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) return setError('Name is required');
    if (!email.trim() || !email.includes('@')) return setError('A valid email is required');
    if (!signature) return setError('Please draw your signature above');

    const payload = {
      quotationId: quotation.ID,
      signatoryName: name.trim(),
      signatoryEmail: email.trim(),
      signatoryCompany: company.trim() || null,
      signatureImage: signature
    };

    if (optionalLines.length > 0) {
      payload.optionalSelections = optionalLines.map((l) => ({
        lineId: l.ID,
        accepted: !!optionSelections[l.ID]
      }));
    }

    onSubmit(payload);
  };

  return (
    <div style={{ background: 'white', borderRadius: '16px', padding: '36px 32px', maxWidth: '560px', margin: '0 auto', border: '1px solid #eee' }}>
      <div style={{ marginBottom: '28px' }}>
        <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', color: '#1a1a1a', fontWeight: 600 }}>
          Accept v{quotation.VersionNumber}
          {quotation.VersionLabel ? ` — ${quotation.VersionLabel}` : ''}
        </h3>
        <p style={{ margin: 0, color: '#999', fontSize: '13px', lineHeight: 1.5 }}>By signing below, you confirm acceptance of the quotation terms and pricing.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {/* Optional Items Section */}
          {optionalLines.length > 0 && (
            <div style={{ border: '1px solid #eee', borderRadius: '12px', padding: '18px 16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '12px', letterSpacing: '0.02em' }}>Optional Items</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {groupByArea(optionalLines, areaConfigs).map((group) => (
                  <div key={group.code}>
                    {/* Area header */}
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px', paddingLeft: '2px' }}>{group.label}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {group.lines.map((line) => (
                        <label
                          key={line.ID}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            background: optionSelections[line.ID] ? '#f8f9fb' : '#fafafa',
                            border: optionSelections[line.ID] ? '1px solid #dde' : '1px solid #eee',
                            cursor: 'pointer',
                            transition: 'background 0.15s, border-color 0.15s'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!optionSelections[line.ID]}
                            onChange={() => toggleOption(line.ID)}
                            style={{ width: '16px', height: '16px', accentColor: '#1a1a1a', flexShrink: 0, marginTop: '2px' }}
                          />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '2px' }}>{getBreadcrumb(areaConfigs, line)}</span>
                            <span style={{ display: 'block', fontSize: '13px', color: '#333' }}>{line.Description || 'Optional item'}</span>
                          </span>
                          <span style={{ flexShrink: 0, textAlign: 'right', paddingTop: '2px' }}>
                            <span style={{ display: 'block', fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>
                              {line.Quantity} x {formatCurrency(line.UnitSellingPrice)}
                            </span>
                            <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>{formatCurrency(lineTotal(line))}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                  <span>Base total</span>
                  <span>{formatCurrency(baseTotal)}</span>
                </div>
                {selectedOptionsTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                    <span>Selected options</span>
                    <span>+ {formatCurrency(selectedOptionsTotal)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 600, color: '#1a1a1a', marginTop: '4px' }}>
                  <span>Total</span>
                  <span>{formatCurrency(baseTotal + selectedOptionsTotal)}</span>
                </div>
                <div style={{ textAlign: 'right', fontSize: '10px', color: '#bbb', marginTop: '1px' }}>excl. VAT</div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px', letterSpacing: '0.02em' }}>Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#1a1a1a')}
                onBlur={(e) => (e.target.style.borderColor = '#e0e0e0')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px', letterSpacing: '0.02em' }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@company.com"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#1a1a1a')}
                onBlur={(e) => (e.target.style.borderColor = '#e0e0e0')}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px', letterSpacing: '0.02em' }}>
              Company <span style={{ fontWeight: 400, color: '#bbb' }}>(optional)</span>
            </label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Company name"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = '#1a1a1a')}
              onBlur={(e) => (e.target.style.borderColor = '#e0e0e0')}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '8px', letterSpacing: '0.02em' }}>Signature</label>
            <SignaturePad onEnd={setSignature} />
          </div>

          {error && <p style={{ color: '#c0392b', fontSize: '13px', margin: 0, padding: '8px 12px', background: '#fdf2f2', borderRadius: '8px' }}>{error}</p>}

          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '11px 28px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
                borderRadius: '10px',
                background: '#1a1a1a',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'background 0.15s, opacity 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!loading) e.target.style.background = '#333';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.target.style.background = '#1a1a1a';
              }}
            >
              {loading ? 'Submitting...' : 'Confirm acceptance'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={{
                padding: '11px 22px',
                fontSize: '14px',
                fontWeight: 500,
                border: '1px solid #ddd',
                borderRadius: '10px',
                background: 'white',
                color: '#666',
                cursor: 'pointer',
                transition: 'border-color 0.15s'
              }}
              onMouseEnter={(e) => (e.target.style.borderColor = '#aaa')}
              onMouseLeave={(e) => (e.target.style.borderColor = '#ddd')}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
